import {
  format,
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  addDays,
  addMonths,
  subMonths,
  isSameMonth,
  isToday,
  set,
} from "date-fns";
import { useEffect, useState } from "react";
import {
  GetStudentDashboardCalander,
  GetStudentDashboardDemoAndClassData,
} from "../../Api/DashboardApi";
import { Divider, Grid } from "@mui/material";
import CustomCardWithBorder from "../../../../components/CustomCardWithBorder";
import ViewUpcomingDemoAndClasses from "./ViewUpcomingDemoAndClasses";
import Legend from "./components/LegendComp";
import Loader from "../../../../../SAPPages/components/Loader/Loader";

export default function UpcommingSessionCalendar() {
  const userId = localStorage.getItem("userId");
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(
    format(new Date(), "yyyy-MM-dd")
  );
  const [preferredDay, setPreferredDay] = useState(new Date().getDate());

  const [evenData, setEvenData] = useState(null);
  const [evenDataLoading, setEvenDataLoading] = useState(false);
  const [upcomingSessions, setUpcomingSessions] = useState([]);
  const [upcomingSessionsLoading, setUpcomingSessionsLoading] = useState(true);

  const changeMonthKeepPreferredDay = (selectedDate, preferredDay, diff) => {
    const base = addMonths(new Date(selectedDate), diff);
    const lastDay = endOfMonth(base).getDate();

    return new Date(
      base.getFullYear(),
      base.getMonth(),
      Math.min(preferredDay, lastDay)
    );
  };

  const handlefetchCalanderEventData = async () => {
    try {
      setEvenDataLoading(true);
      const response = await GetStudentDashboardCalander({
        user_id: parseInt(userId),
        date: currentMonth,
      });

      if (response.status === 200) {
        setEvenData(response?.data);
        setEvenDataLoading(false);
      } else {
        setEvenData([]);
        setEvenDataLoading(false);
      }
    } catch (error) {
      setEvenData([]);
      setEvenDataLoading(false);
    }
  };

  useEffect(() => {
    handlefetchCalanderEventData();
  }, [currentMonth]);

  const handlefetchUpcomingSessionsData = async () => {
    try {
      setUpcomingSessionsLoading(true);
      const response = await GetStudentDashboardDemoAndClassData({
        user_id: parseInt(userId),
        date: selectedDate,
      });

      if (response.status === 200) {
        setUpcomingSessions(response?.data);

        setUpcomingSessionsLoading(false);
      } else {
        setUpcomingSessions([]);
        setUpcomingSessionsLoading(false);
      }
    } catch (error) {
      setUpcomingSessions([]);
      setUpcomingSessionsLoading(false);
    }
  };

  useEffect(() => {
    handlefetchUpcomingSessionsData();
  }, [selectedDate, currentMonth]);

  const getEventColor = (dateKey) => {
    const event = evenData?.find((e) => e.date === dateKey);

    if (!event) return null;

    if (event.is_upcoming_demo) return "green";
    if (event.is_upcoming_class) return "green";
    if (event.is_attended_demo) return "orange";
    if(event?.is_attended_class) return "orange";

    return null;
  };

  const monthStart = startOfMonth(currentMonth);
  const monthEnd = endOfMonth(monthStart);
  const startDate = startOfWeek(monthStart, { weekStartsOn: 1 });
  const endDate = endOfWeek(monthEnd, { weekStartsOn: 1 });

  const rows = [];
  let day = startDate;

  while (day <= endDate) {
    const days = [];
    for (let i = 0; i < 7; i++) {
      days.push(day);
      day = addDays(day, 1);
    }
    rows.push(days);
  }

  return (
    <CustomCardWithBorder padding="10px" >
      <Grid container>
        <Grid item xs={12} sm={12} md={12}>
          <div >
            {/* Header */}
            <div className="flex items-center justify-between mb-4">
              <button
                onClick={() => {
                  const newDate = changeMonthKeepPreferredDay(
                    selectedDate,
                    preferredDay,
                    -1
                  );
                  setCurrentMonth(newDate);
                  setSelectedDate(format(newDate, "yyyy-MM-dd"));
                }}
                className="w-8 h-8 rounded-full bg-[#F3F3FF] text-[#312182] text-[1.65rem]"
              >
                ‹
              </button>
              <h2 className="font-[600] text-[18px] text-[#161719] fontfamily-Inter">
                {format(currentMonth, "MMMM yyyy")}
              </h2>
              <button
                onClick={() => {
                  const newDate = changeMonthKeepPreferredDay(
                    selectedDate,
                    preferredDay,
                    1
                  );
                  setCurrentMonth(newDate);
                  setSelectedDate(format(newDate, "yyyy-MM-dd"));
                }}
                className="w-8 h-8 rounded-full bg-[#F3F3FF] text-[#312182] text-[1.65rem]"
              >
                ›
              </button>
            </div>

{evenDataLoading ? (
  <Loader text="Loading Sessions..." height="20vh"/>
) : (
  
<>  {/* Week Days */}
            <div className="grid grid-cols-7 text-xs text-[#5F6570] fontfamily-Inter font-[700] mb-2">
              {["MON", "TUE", "WED", "THU", "FRI", "SAT", "SUN"].map((d) => (
                <div key={d} className="text-center">
                  {d}
                </div>
              ))}
            </div>

            {/* Dates */}
            <div className="space-y-2">
              {rows.map((week, i) => (
                <div key={i} className="grid grid-cols-7 gap-1">
                  {week.map((date, idx) => {
                    const key = format(date, "yyyy-MM-dd");
                    const dotColor = getEventColor(key);
                    const isSelected = selectedDate === key;
                    const isTodayDate = isToday(date);

                    return (
                      <button
                        key={idx}
                        onClick={() => {
                          setSelectedDate(key);
                          setPreferredDay(new Date(key).getDate());
                        }}
                        className={`
    relative w-8 h-8 flex flex-col items-center justify-center rounded-lg text-sm
    ${!isSameMonth(date, monthStart) ? "text-gray-300" : ""}
    ${isTodayDate ? "bg-[#572575] text-white" : "text-[#161719]"}
        ${!isTodayDate && isSelected ? " text-[#9D0385]" : "text-[#161719]"}
  `}
                        style={{
                          border: dotColor
                            ? "1px solid #572575"
                            : !isTodayDate && isSelected
                            ? "1px solid #9D0385"
                            : "none",
                        }}
                      >
                        <span>{format(date, "dd")}</span>

                        {dotColor && (
                          <span
                            className={`absolute top-[-0.38rem] right-[-0.15rem] w-2 h-2 rounded-full mt-1
                        ${dotColor === "blue" && "bg-[#357BE0]"}
                        ${dotColor === "green" && "bg-[#1FAD77]"}
                        ${dotColor === "orange" && "bg-[#B9782E]"}
                        ${dotColor === "purple" && "bg-purple-700"}
                      `}
                          />
                        )}
                      </button>
                    );
                  })}
                </div>
              ))}
            </div>
</>
          
)}
            {/* Legend */}
            <div
              className="flex justify-evenly items-center  text-[13px] font-[600] mt-5 fontfamily-Inter gap-3"
              style={{ color: "#313439" }}
            >
              {/* <Legend color="blue" label="Upcoming Demo" /> */}
              <Legend color="green" label="Upcoming Demo / Class" />
              <Legend color="orange" label="Attended Demo / Class" />
            </div>
            <div>
              <Divider
                sx={{
                  borderTop: "1px dashed #999999", // dashed line
                  my: 2, // vertical margin
                }}
              />
            </div>

            {/* Today Session */}
            <div className="mt-3 ">
              <ViewUpcomingDemoAndClasses
                demoAndClassData={upcomingSessions}
                upcomingSessionsLoading={upcomingSessionsLoading}
                selectedDate={selectedDate}
              />
            </div>
          </div>
        </Grid>
      </Grid>
    </CustomCardWithBorder>
  );
}
